import React, { useState, useRef, useEffect } from 'react';
import { X, Package, Loader2, Truck, MapPin, Clock, Globe } from 'lucide-react';

// 中文结果接口
interface TrackingResultCN {
    "快递单号": string;
    "当前物流状态": string;
    "最新操作时间": string;
    "派件员信息": string;
    "整体物流周期": string;
    "物流轨迹": Array<{
        "操作时间": string;
        "操作地点": string;
        "操作状态": string;
        "详细说明": string;
    }>;
}

// 英文结果接口
interface TrackingResultEN {
    "Tracking Number": string;
    "Current Logistics Status": string;
    "Latest Operation Time": string;
    "Courier Information": string;
    "Overall Logistics Cycle": string;
    "Logistics Track": Array<{
        "Operation Time": string;
        "Location": string;
        "Operation Status": string;
        "Detailed Description": string;
    }>;
}

interface TrackingModalProps {
    onClose: () => void;
}

const TrackingModal: React.FC<TrackingModalProps> = ({ onClose }) => {
    const [trackingNumbers, setTrackingNumbers] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [resultsCN, setResultsCN] = useState<TrackingResultCN[]>([]);
    const [resultsEN, setResultsEN] = useState<TrackingResultEN[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [lang, setLang] = useState<'cn' | 'en'>('cn');
    const modalRef = useRef<HTMLDivElement>(null);

    // 点击外部关闭
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const handleQuery = async () => {
        const numbers = trackingNumbers
            .split(/[,\n]/)
            .map((n: string) => n.trim())
            .filter((n: string) => n.length > 0);

        if (numbers.length === 0) {
            setError('请输入至少一个运单号');
            return;
        }

        setIsLoading(true);
        setError(null);
        setResultsCN([]);
        setResultsEN([]);

        try {
            const response = await fetch('/api/tracking/query', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ tracking_numbers: numbers, lang }),
            });

            const data = await response.json();

            if (data.success) {
                if (lang === 'cn') {
                    setResultsCN(data.data || []);
                } else {
                    setResultsEN(data.data || []);
                }
                if (data.data?.length === 0) {
                    setError('未找到物流信息');
                }
            } else {
                setError(data.detail || '查询失败');
            }
        } catch (err) {
            setError('网络错误，请重试');
            console.error('Tracking query error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div ref={modalRef} className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-primary" />
                        <h2 className="text-lg font-semibold">物流查询</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* 中英切换开关 */}
                        <button
                            onClick={() => setLang(lang === 'cn' ? 'en' : 'cn')}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-muted rounded-md hover:bg-muted/80"
                            title="切换语言"
                        >
                            <Globe className="w-3 h-3" />
                            <span>{lang === 'cn' ? '中文' : 'EN'}</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-muted rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 flex-1 overflow-y-auto">
                    {/* Input */}
                    <div className="space-y-3 mb-4">
                        <label className="block text-sm font-medium">
                            输入运单号
                            <span className="text-muted-foreground text-xs ml-2">
                                (多个运单号用逗号或换行分隔)
                            </span>
                        </label>
                        <textarea
                            value={trackingNumbers}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTrackingNumbers(e.target.value)}
                            placeholder="例如：86254200001257&#10;86254200001258"
                            className="w-full h-24 p-3 bg-input border border-border rounded-md focus:ring-2 focus:ring-ring focus:outline-none resize-none"
                        />
                        <button
                            onClick={handleQuery}
                            disabled={isLoading || !trackingNumbers.trim()}
                            className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    查询中...
                                </>
                            ) : (
                                <>
                                    <Truck className="w-4 h-4" />
                                    查询
                                </>
                            )}
                        </button>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    {/* 中文结果 */}
                    {lang === 'cn' && resultsCN.length > 0 && (
                        <div className="space-y-4">
                            {resultsCN.map((result, index) => (
                                <div key={index} className="bg-muted/50 rounded-lg p-4">
                                    {/* Summary */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <p className="font-mono font-semibold text-primary">
                                                {result["快递单号"]}
                                            </p>
                                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                <Clock className="w-3 h-3" />
                                                {result["整体物流周期"]}
                                            </p>
                                        </div>
                                        <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                                            {result["当前物流状态"]}
                                        </span>
                                    </div>

                                    {/* Latest Info */}
                                    <div className="text-sm mb-3 p-2 bg-card rounded-md">
                                        <p className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-muted-foreground" />
                                            <span className="font-medium">最新：</span>
                                            {result["最新操作时间"]}
                                        </p>
                                        {result["派件员信息"] !== "暂无" && (
                                            <p className="mt-1 text-muted-foreground">
                                                派件员：{result["派件员信息"]}
                                            </p>
                                        )}
                                    </div>

                                    {/* Timeline */}
                                    {result["物流轨迹"]?.length > 0 && (
                                        <div className="border-l-2 border-border pl-4 space-y-3 mt-3">
                                            {result["物流轨迹"].slice().reverse().map((track, trackIndex) => (
                                                <div key={trackIndex} className="relative">
                                                    <div className="absolute -left-[1.35rem] w-2 h-2 rounded-full bg-primary" />
                                                    <p className="text-xs text-muted-foreground">
                                                        {track["操作时间"]}
                                                    </p>
                                                    <p className="text-sm font-medium">
                                                        {track["操作状态"]}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {track["详细说明"]}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 英文结果 */}
                    {lang === 'en' && resultsEN.length > 0 && (
                        <div className="space-y-4">
                            {resultsEN.map((result, index) => (
                                <div key={index} className="bg-muted/50 rounded-lg p-4">
                                    {/* Summary */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <p className="font-mono font-semibold text-primary">
                                                {result["Tracking Number"]}
                                            </p>
                                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                <Clock className="w-3 h-3" />
                                                {result["Overall Logistics Cycle"]}
                                            </p>
                                        </div>
                                        <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                                            {result["Current Logistics Status"]}
                                        </span>
                                    </div>

                                    {/* Latest Info */}
                                    <div className="text-sm mb-3 p-2 bg-card rounded-md">
                                        <p className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-muted-foreground" />
                                            <span className="font-medium">Latest:</span>
                                            {result["Latest Operation Time"]}
                                        </p>
                                        {result["Courier Information"] !== "Not Available" && (
                                            <p className="mt-1 text-muted-foreground">
                                                Courier: {result["Courier Information"]}
                                            </p>
                                        )}
                                    </div>

                                    {/* Timeline */}
                                    {result["Logistics Track"]?.length > 0 && (
                                        <div className="border-l-2 border-border pl-4 space-y-3 mt-3">
                                            {result["Logistics Track"].slice().reverse().map((track, trackIndex) => (
                                                <div key={trackIndex} className="relative">
                                                    <div className="absolute -left-[1.35rem] w-2 h-2 rounded-full bg-primary" />
                                                    <p className="text-xs text-muted-foreground">
                                                        {track["Operation Time"]}
                                                    </p>
                                                    <p className="text-sm font-medium">
                                                        {track["Operation Status"]}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {track["Detailed Description"]}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border">
                    <button
                        onClick={onClose}
                        className="w-full py-2 px-4 bg-muted text-foreground rounded-md hover:bg-muted/80"
                    >
                        关闭
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrackingModal;
